import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  MessageSquare, 
  Send, 
  Plus, 
  Paperclip, 
  Download,
  User,
  Clock,
  Loader2,
  X,
  FileText,
  ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import { exportConversationPdf } from '../lib/exportData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Messages = () => {
  const { user, isAdmin } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [isNewMessageOpen, setIsNewMessageOpen] = useState(false);
  const [newMessage, setNewMessage] = useState({
    recipient_id: '',
    subject: '',
    content: '',
  });
  const [replyContent, setReplyContent] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [replyAttachments, setReplyAttachments] = useState([]);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const replyFileInputRef = useRef(null);

  const MAX_ATTACHMENTS = 3;

  useEffect(() => {
    fetchConversations();
    fetchUsers();
  }, []);

  useEffect(() => {
    if (selectedConversation) {
      fetchConversationMessages(selectedConversation.partner_id);
    }
  }, [selectedConversation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async () => {
    try {
      const response = await fetch(`${API}/messages/conversations`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setConversations(data);
      }
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API}/users/list`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchConversationMessages = async (partnerId) => {
    try {
      const response = await fetch(`${API}/messages/conversation/${partnerId}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages);
        // Refresh conversations to update unread count
        fetchConversations();
      }
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const handleFileChange = (e, isReply = false) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const target = isReply ? replyAttachments : attachments;
    if (target.length + files.length > MAX_ATTACHMENTS) {
      toast.error(`Maximum ${MAX_ATTACHMENTS} pieces jointes par message`);
      return;
    }
    const filtered = files.filter(f => {
      if (f.size > 10 * 1024 * 1024) {
        toast.error(`${f.name}: 10 Mo max`);
        return false;
      }
      return true;
    });
    Promise.all(filtered.map(f => new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve({
        name: f.name,
        data: reader.result.split(',')[1],
        type: f.name.split('.').pop().toLowerCase(),
      });
      reader.readAsDataURL(f);
    }))).then(newAtts => {
      if (isReply) setReplyAttachments(prev => [...prev, ...newAtts]);
      else setAttachments(prev => [...prev, ...newAtts]);
    });
    // Reset input so same file can be re-selected
    e.target.value = '';
  };

  const removeAttachment = (index, isReply = false) => {
    if (isReply) setReplyAttachments(prev => prev.filter((_, i) => i !== index));
    else setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const sendNewMessage = async () => {
    if (!newMessage.recipient_id || !newMessage.subject || !newMessage.content) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setSending(true);
    try {
      const payload = {
        ...newMessage,
        attachments: attachments,
      };

      const response = await fetch(`${API}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        toast.success('Message envoye');
        setIsNewMessageOpen(false);
        setNewMessage({ recipient_id: '', subject: '', content: '' });
        setAttachments([]);
        fetchConversations();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de l\'envoi');
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Erreur de connexion');
    } finally {
      setSending(false);
    }
  };

  const sendReply = async () => {
    if (!replyContent.trim()) {
      toast.error('Veuillez ecrire un message');
      return;
    }

    if (!messages.length) return;

    setSending(true);
    try {
      const lastMessage = messages[messages.length - 1];
      const payload = {
        content: replyContent,
        attachments: replyAttachments,
      };

      const response = await fetch(`${API}/messages/${lastMessage.message_id}/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setReplyContent('');
        setReplyAttachments([]);
        fetchConversationMessages(selectedConversation.partner_id);
      } else {
        toast.error('Erreur lors de l\'envoi');
      }
    } catch (error) {
      console.error('Failed to send reply:', error);
      toast.error('Erreur de connexion');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Hier';
    } else if (diffDays < 7) {
      return date.toLocaleDateString('fr-FR', { weekday: 'long' });
    }
    return date.toLocaleDateString('fr-FR');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-[Manrope]" data-testid="messages-title">
            Messagerie
          </h1>
          <p className="text-slate-500 mt-1">
            {isAdmin ? 'Communiquez avec vos partenaires' : 'Contactez l\'administration'}
          </p>
        </div>
        
        <Dialog open={isNewMessageOpen} onOpenChange={setIsNewMessageOpen}>
          <DialogTrigger asChild>
            <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="new-message-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouveau message
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Nouveau message</DialogTitle>
              <DialogDescription>
                Envoyez un message a {isAdmin ? 'un partenaire' : 'l\'administration'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Destinataire *</Label>
                <Select
                  value={newMessage.recipient_id}
                  onValueChange={(value) => setNewMessage({ ...newMessage, recipient_id: value })}
                >
                  <SelectTrigger data-testid="recipient-select">
                    <SelectValue placeholder="Selectionner un destinataire" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map((u) => {
                      const roleLabels = {
                        sa: 'Super Admin',
                        soa: 'Sous-Admin',
                        admin: 'Admin',
                        rh: 'RH',
                        commercial: 'Agent Commercial'
                      };
                      const roleColors = {
                        sa: 'bg-amber-100 text-amber-700',
                        soa: 'bg-violet-100 text-violet-700',
                        admin: 'bg-violet-100 text-violet-700',
                        rh: 'bg-emerald-100 text-emerald-700',
                        commercial: 'bg-slate-100 text-slate-700'
                      };
                      return (
                        <SelectItem key={u.user_id} value={u.user_id}>
                          <div className="flex items-center gap-2">
                            <span>{u.name}</span>
                            <Badge className={`text-xs ${roleColors[u.role] || 'bg-slate-100 text-slate-700'}`}>
                              {roleLabels[u.role] || u.role}
                            </Badge>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Sujet *</Label>
                <Input
                  value={newMessage.subject}
                  onChange={(e) => setNewMessage({ ...newMessage, subject: e.target.value })}
                  placeholder="Objet du message"
                  data-testid="message-subject"
                />
              </div>
              <div className="space-y-2">
                <Label>Message *</Label>
                <Textarea
                  value={newMessage.content}
                  onChange={(e) => setNewMessage({ ...newMessage, content: e.target.value })}
                  placeholder="Ecrivez votre message..."
                  rows={5}
                  data-testid="message-content"
                />
              </div>
              <div className="space-y-2">
                <Label>Pieces jointes ({attachments.length}/{MAX_ATTACHMENTS})</Label>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={(e) => handleFileChange(e, false)}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                  multiple
                />
                {attachments.length > 0 && (
                  <div className="space-y-2">
                    {attachments.map((att, i) => (
                      <div key={i} className="flex items-center gap-2 p-3 bg-slate-100 rounded-lg" data-testid={`attachment-${i}`}>
                        <FileText className="w-4 h-4 text-slate-500" />
                        <span className="text-sm flex-1 truncate">{att.name}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeAttachment(i, false)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                {attachments.length < MAX_ATTACHMENTS && (
                  <Button
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full"
                    data-testid="attach-file-btn"
                  >
                    <Paperclip className="w-4 h-4 mr-2" />
                    Joindre un fichier (max {MAX_ATTACHMENTS})
                  </Button>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsNewMessageOpen(false)}>
                Annuler
              </Button>
              <Button onClick={sendNewMessage} disabled={sending} data-testid="send-message-btn">
                {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Envoyer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[calc(100vh-220px)] min-h-[500px]">
        {/* Conversations list */}
        <Card className={`border-0 shadow-sm lg:col-span-1 flex-col ${selectedConversation ? 'hidden lg:flex' : 'flex'}`}>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-[Manrope]">Conversations</CardTitle>
          </CardHeader>
          <ScrollArea className="flex-1">
            <div className="px-4 pb-4 space-y-2">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : conversations.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>Aucune conversation</p>
                </div>
              ) : (
                conversations.map((conv) => (
                  <button
                    key={conv.partner_id}
                    onClick={() => setSelectedConversation(conv)}
                    className={`w-full p-3 rounded-xl text-left transition-all ${
                      selectedConversation?.partner_id === conv.partner_id
                        ? 'bg-indigo-100 dark:bg-indigo-900/30'
                        : 'hover:bg-slate-100 dark:hover:bg-slate-800'
                    }`}
                    data-testid={`conversation-${conv.partner_id}`}
                  >
                    <div className="flex items-start gap-3">
                      <Avatar className="w-10 h-10">
                        <AvatarImage src={conv.partner?.picture} />
                        <AvatarFallback className="bg-indigo-100 text-indigo-600">
                          {conv.partner?.name?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-sm truncate">{conv.partner?.name}</p>
                          {conv.unread_count > 0 && (
                            <Badge className="bg-indigo-600 text-white text-xs">
                              {conv.unread_count}
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 truncate mt-1">
                          {conv.last_message?.subject}
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                          {formatDate(conv.last_message?.created_at)}
                        </p>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </ScrollArea>
        </Card>

        {/* Message thread */}
        <Card className={`border-0 shadow-sm lg:col-span-2 flex-col ${selectedConversation ? 'flex' : 'hidden lg:flex'}`}>
          {selectedConversation ? (
            <>
              <CardHeader className="pb-3 border-b">
                <div className="flex items-center gap-3">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="lg:hidden -ml-2 shrink-0"
                    onClick={() => setSelectedConversation(null)}
                    data-testid="back-to-conversations-btn"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </Button>
                  <Avatar>
                    <AvatarImage src={selectedConversation.partner?.picture} />
                    <AvatarFallback className="bg-indigo-100 text-indigo-600">
                      {selectedConversation.partner?.name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <CardTitle className="text-lg font-[Manrope]">
                      {selectedConversation.partner?.name}
                    </CardTitle>
                    <p className="text-sm text-slate-500">
                      {(() => {
                        const roleLabels = {
                          sa: 'Super Admin',
                          soa: 'Sous-Admin',
                          admin: 'Administrateur',
                          rh: 'RH',
                          commercial: 'Agent Commercial'
                        };
                        return roleLabels[selectedConversation.partner?.role] || 'Utilisateur';
                      })()}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="ml-auto shrink-0"
                    onClick={() => exportConversationPdf(selectedConversation.partner_id)}
                    data-testid="export-conversation-pdf-btn"
                  >
                    <FileText className="w-4 h-4 mr-1.5" /> PDF certifié
                  </Button>
                </div>
              </CardHeader>
              
              <ScrollArea className="flex-1 p-4">
                <div className="space-y-4">
                  {messages.map((msg, index) => {
                    const isOwn = msg.sender_id === user?.user_id;
                    return (
                      <div
                        key={msg.message_id}
                        className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[80%] p-4 rounded-2xl ${
                            isOwn
                              ? 'bg-indigo-600 text-white'
                              : 'bg-slate-100 dark:bg-slate-800'
                          }`}
                        >
                          {index === 0 || messages[index - 1]?.subject !== msg.subject ? (
                            <p className={`font-semibold mb-2 ${isOwn ? 'text-indigo-100' : 'text-slate-700 dark:text-slate-200'}`}>
                              {msg.subject}
                            </p>
                          ) : null}
                          <p className={`text-sm whitespace-pre-wrap break-words ${isOwn ? 'text-white' : 'text-slate-600 dark:text-slate-300'}`}>
                            {msg.content}
                          </p>
                          {((msg.attachments && msg.attachments.length > 0) || msg.attachment) && (
                            <div className="mt-3 space-y-2">
                              {(msg.attachments && msg.attachments.length > 0 ? msg.attachments : [msg.attachment]).map((att, i) => (
                                <a
                                  key={i}
                                  href={`data:application/${att.type || 'octet-stream'};base64,${att.data}`}
                                  download={att.name}
                                  className={`flex items-center gap-2 p-2 rounded-lg ${
                                    isOwn ? 'bg-indigo-500 hover:bg-indigo-400' : 'bg-slate-200 hover:bg-slate-300'
                                  }`}
                                >
                                  <Download className="w-4 h-4" />
                                  <span className="text-xs truncate">{att.name}</span>
                                </a>
                              ))}
                            </div>
                          )}
                          <p className={`text-xs mt-2 ${isOwn ? 'text-indigo-200' : 'text-slate-400'}`}>
                            {formatDate(msg.created_at)}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>

              {/* Reply input */}
              <div className="p-4 border-t">
                <div className="flex gap-3">
                  <div className="flex-1 space-y-2">
                    <Textarea
                      value={replyContent}
                      onChange={(e) => setReplyContent(e.target.value)}
                      placeholder="Ecrivez votre reponse..."
                      rows={2}
                      className="resize-none"
                      data-testid="reply-input"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          sendReply();
                        }
                      }}
                    />
                    {replyAttachments.length > 0 && (
                      <div className="space-y-1">
                        {replyAttachments.map((att, i) => (
                          <div key={i} className="flex items-center gap-2 p-2 bg-slate-100 rounded-lg">
                            <FileText className="w-4 h-4 text-slate-500" />
                            <span className="text-xs flex-1 truncate">{att.name}</span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => removeAttachment(i, true)}
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <input
                      type="file"
                      ref={replyFileInputRef}
                      onChange={(e) => handleFileChange(e, true)}
                      className="hidden"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                      multiple
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => replyFileInputRef.current?.click()}
                      title={`Joindre un fichier (${replyAttachments.length}/${MAX_ATTACHMENTS})`}
                      disabled={replyAttachments.length >= MAX_ATTACHMENTS}
                      data-testid="reply-attach-btn"
                    >
                      <Paperclip className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={sendReply}
                      disabled={sending || !replyContent.trim()}
                      size="icon"
                      className="bg-indigo-600 hover:bg-indigo-700"
                      data-testid="send-reply-btn"
                    >
                      {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageSquare className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <p className="text-slate-500">Selectionnez une conversation</p>
                <p className="text-sm text-slate-400 mt-1">ou creez un nouveau message</p>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};
